/**
 * <cart-aov-bundle>
 * Suggests complementary products in the cart to raise average order value.
 *
 * - Anchors Shopify `intent: complementary` recommendations on a cart item.
 * - Fetches sections/cart-aov-bundle.liquid and injects the [data-bundle-list].
 * - Hides products already in the cart; hides itself when the cart is empty.
 * - Refreshes on every Minimog cart change (and when the drawer opens).
 * - One-tap AJAX add that re-renders the cart drawer the Minimog way.
 *
 * Runs alongside the Sympl widget; no external dependencies beyond Minimog's pub/sub.
 */
(function () {
  if (customElements.get('cart-aov-bundle')) return;

  class CartAovBundle extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-bundle-list]');
      this.sectionId = this.dataset.sectionId || 'cart-aov-bundle-data';
      this.limit = parseInt(this.dataset.limit, 10) || 4;
      this.anchorMode = this.dataset.anchor || 'first';

      this.onClick = this.onClick.bind(this);
      this.addEventListener('click', this.onClick);

      if (window.MinimogEvents && window.MinimogTheme && window.MinimogTheme.pubSubEvents) {
        const events = window.MinimogTheme.pubSubEvents;
        this.unsubscribers = [
          window.MinimogEvents.subscribe(events.cartUpdate, () => this.refresh()),
          window.MinimogEvents.subscribe(events.openCartDrawer, () => this.refresh()),
        ];
      }

      this.refresh();
    }

    disconnectedCallback() {
      (this.unsubscribers || []).forEach((off) => {
        if (typeof off === 'function') off();
      });
    }

    get root() {
      return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    }

    async refresh() {
      try {
        const cart = await fetch(`${this.root}cart.js`, {
          headers: { Accept: 'application/json' },
        }).then((response) => response.json());

        if (!cart || !cart.item_count) {
          this.hidden = true;
          return;
        }

        const anchorId = this.pickAnchor(cart);
        if (!anchorId) {
          this.hidden = true;
          return;
        }

        const url =
          `${this.root}recommendations/products` +
          `?section_id=${encodeURIComponent(this.sectionId)}` +
          `&product_id=${anchorId}` +
          `&intent=complementary` +
          `&limit=${this.limit}`;

        const markup = await fetch(url).then((response) => response.text());
        const fetchedList = new DOMParser()
          .parseFromString(markup, 'text/html')
          .querySelector('[data-bundle-list]');

        if (!fetchedList) {
          this.hidden = true;
          return;
        }

        const productsInCart = new Set(cart.items.map((item) => String(item.product_id)));
        fetchedList.querySelectorAll('[data-bundle-item]').forEach((item) => {
          if (productsInCart.has(item.dataset.productId)) item.remove();
        });

        this.list.innerHTML = fetchedList.innerHTML;
        this.hidden = this.list.children.length === 0;
      } catch (error) {
        this.hidden = true;
      }
    }

    pickAnchor(cart) {
      if (!cart.items || !cart.items.length) return null;
      if (this.anchorMode === 'highest') {
        return cart.items.reduce((best, item) =>
          item.final_line_price > best.final_line_price ? item : best
        ).product_id;
      }
      return cart.items[0].product_id;
    }

    async onClick(event) {
      const button = event.target.closest('[data-bundle-add]');
      if (!button) return;
      event.preventDefault();

      const variantId = button.dataset.variantId;
      if (!variantId || button.disabled) return;

      button.disabled = true;
      button.classList.add('is-loading');

      const drawer = document.querySelector('m-cart-drawer');
      const body = { items: [{ id: variantId, quantity: 1 }] };
      if (drawer && typeof drawer.getSectionsToRender === 'function') {
        body.sections = drawer.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
      }

      try {
        const result = await fetch(`${this.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        }).then((response) => response.json());

        if (!result || !result.status) {
          if (drawer && typeof drawer.renderContents === 'function') drawer.renderContents(result);
          if (window.MinimogEvents && window.MinimogTheme && window.MinimogTheme.pubSubEvents) {
            window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartUpdate, result);
          }
        }
      } catch (error) {
        /* fail quietly — the button is restored below */
      } finally {
        button.disabled = false;
        button.classList.remove('is-loading');
      }
    }
  }

  customElements.define('cart-aov-bundle', CartAovBundle);
})();
